import { db } from '@app/icare/client/db'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import {
  type PatientMasterDataSheet,
  PatientMasterDataSheetStatusEnum,
} from '@app/icare/db/schema/form/PatientMasterDataSheet'
import { selectByFindingId } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/Patient'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import type { UserIdType } from '@app/icare/entities/user/schema/User'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { formatDateTime } from '@pkg/shared/utils'
import { AddButton, ButtonLink } from '@pkg/ui/button'
import { useExtra } from '@pkg/ui/layout/PageContainerRuntime'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Flex, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { FaPencil } from 'react-icons/fa6'
import { getRecipientLabel, recipientRelationsQueryOptions } from './_forms/stammblatt/-utils/recipient'
import { sortByDateTime } from './-utils/sorters'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/stammblatt')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

function RouteComponent() {
  const { id: patientId } = Route.useParams()

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll()
        .$call(queryKit.deleted('exclude'))
        .where('patientId', '=', patientId)
        .where('type', '=', PatientFormTypeEnum.MasterDataSheet),
      params: queryKit.parseQuery({}),
      refetchOnMount: true,
    }),
  )
  useQuery(recipientRelationsQueryOptions(patientId))

  const columns: ColumnsType<PatientMasterDataSheet> = useMemo(
    () => [
      {
        key: 'actions',
        width: 60,
        fixed: 'left',
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/stammblatt/$form"
            params={{
              id: record.patientId,
              form: record.id,
            }}
            type="text"
            icon={<FaPencil />}
            size="small"
          />
        ),
      },
      {
        key: 'recipient',
        title: <Trans>Empfänger</Trans>,
        dataIndex: ['form', 'recipient'],
        width: 200,
        sorter: (a, b) => {
          const getLabel = (record: PatientMasterDataSheet) =>
            getRecipientLabel(patientId, record.form.recipient, t`keine Angabe`)
          return getLabel(a).localeCompare(getLabel(b))
        },
        render: (_, record) => getRecipientLabel(patientId, record.form.recipient, t`keine Angabe`),
      },
      {
        key: 'status',
        title: <Trans>Status</Trans>,
        width: 120,
        sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
        render: (_, record) => <FormStatusBadge value={record.status} statusEnum={PatientMasterDataSheetStatusEnum} />,
      },
      {
        key: 'createdBy',
        title: <Trans>Erstellt von</Trans>,
        dataIndex: 'createdBy',
        width: 150,
        sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
        render: (userId: UserIdType | null | undefined) => <UserInfo userId={userId} />,
      },
      {
        key: 'createdAt',
        title: <Trans>Erstellt am</Trans>,
        dataIndex: 'createdAt',
        width: 180,
        defaultSortOrder: 'descend',
        sorter: (a, b) => sortByDateTime(a.createdAt, b.createdAt),
        render: (value: string | null | undefined) => formatDateTime(value),
      },
      {
        key: 'updatedBy',
        title: <Trans>Geändert von</Trans>,
        dataIndex: 'updatedBy',
        width: 150,
        sorter: (a, b) => (a.updatedBy ?? '').localeCompare(b.updatedBy ?? ''),
        render: (userId: UserIdType | null | undefined) => <UserInfo userId={userId} />,
      },
      {
        key: 'updatedAt',
        title: <Trans>Geändert am</Trans>,
        dataIndex: 'updatedAt',
        width: 180,
        sorter: (a, b) => sortByDateTime(a.updatedAt, b.updatedAt),
        render: (value: string | null | undefined) => formatDateTime(value),
      },
    ],
    [patientId],
  )

  useExtra(
    <AddButton
      size="small"
      to="/patienten/$id/stammblatt/new"
      params={{
        id: patientId,
      }}
    >
      <Trans>Stammblatt erstellen</Trans>
    </AddButton>,
    [patientId],
  )

  return (
    <Flex vertical gap="small">
      <FindingsSelector selector={selectByFindingId('FormsPatientMasterDataSheetRequired')}>
        {f => <FindingsAlert findings={f} />}
      </FindingsSelector>
      <Table<PatientMasterDataSheet>
        columns={columns}
        dataSource={(data?.data as PatientMasterDataSheet[]) ?? []}
        loading={isFetching}
        rowKey="id"
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          showTotal: total => t`Gesamt ${total}`,
        }}
        scroll={{ x: 'max-content' }}
        size="small"
      />
    </Flex>
  )
}
